/**
 * utils/conditionOrderUtils.ts
 *
 * Utilities for detecting and resolving ordering conflicts in forms with
 * intra-form conditional fields. A conflict occurs when a conditional field
 * is positioned before the source field it depends on.
 *
 * Used by NeedsListEditor and CreateFormTemplate to enforce that conditional
 * fields always appear after their source in the builder (WYSIWYG).
 *
 * Feb 2026 (UX): Added getOrderConflictDetails (contextual drag feedback)
 * and findFirstOrderConflict (specific toast messages on auto-sort).
 * Removed wouldCreateOrderConflict (superseded by getOrderConflictDetails).
 * Feb 2026 (quality): topologicalSortNeeds now detects cycles via a "visiting"
 * stack to avoid silent incorrect ordering on corrupt data. getConditionDependencyMap
 * no longer mutates shared array references (spread instead of push).
 * Mar 2026: Updated all functions to support conditions[] array (multi-source OR logic).
 * A field with multiple conditions must appear after ALL its source fields.
 */

import type { NeedItem } from '../types/needs';

/**
 * Returns all source field IDs for a given NeedItem (multi-condition support).
 */
function getConditionSourceIds(need: NeedItem): string[] {
  if (!need.conditions) return [];
  return need.conditions.map((c) => c.source_field_id);
}

/**
 * Topological sort of NeedItem[], ensuring each source field comes before
 * the fields it conditions. Original position is the tiebreaker for
 * unrelated fields, preserving the user's manual ordering as much as possible.
 * Recalculates position values (0, 1, 2...) on the result.
 * Updated Mar 2026: visits all condition sources (multi-source support).
 */
export function topologicalSortNeeds(needs: NeedItem[]): NeedItem[] {
  const fieldMap = new Map(needs.map((n) => [n.id, n]));
  const visited = new Set<string>();
  const visiting = new Set<string>();
  const result: NeedItem[] = [];

  function visit(need: NeedItem) {
    if (visited.has(need.id)) return;
    if (visiting.has(need.id)) {
      console.warn(
        `[topologicalSortNeeds] Cyclic dependency detected involving need id "${need.id}". Skipping to avoid infinite recursion.`
      );
      return;
    }
    visiting.add(need.id);
    for (const sourceId of getConditionSourceIds(need)) {
      if (fieldMap.has(sourceId)) {
        visit(fieldMap.get(sourceId)!);
      }
    }
    visiting.delete(need.id);
    visited.add(need.id);
    result.push(need);
  }

  const sorted = [...needs].sort((a, b) => a.position - b.position);
  for (const need of sorted) {
    visit(need);
  }

  return result.map((need, index) => ({ ...need, position: index }));
}

/**
 * Returns true if any conditional field is positioned before any of its source fields.
 * Updated Mar 2026: checks all condition sources.
 */
export function detectOrderConflicts(needs: NeedItem[]): boolean {
  const positionMap = new Map(needs.map((n, i) => [n.id, i]));
  for (const need of needs) {
    for (const sourceId of getConditionSourceIds(need)) {
      const sourceIndex = positionMap.get(sourceId);
      const ownIndex = positionMap.get(need.id)!;
      if (sourceIndex !== undefined && sourceIndex > ownIndex) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Simulates moving draggedIndex to targetIndex and returns contextual
 * conflict details: whether there is a conflict and a human-readable reason
 * naming the specific field involved. Used for inline drag-and-drop feedback.
 * Updated Mar 2026: checks all condition sources.
 */
export function getOrderConflictDetails(
  draggedIndex: number,
  targetIndex: number,
  needs: NeedItem[]
): { hasConflict: boolean; reason: string } {
  if (draggedIndex === targetIndex) return { hasConflict: false, reason: '' };

  const newNeeds = [...needs];
  const [draggedNeed] = newNeeds.splice(draggedIndex, 1);
  newNeeds.splice(targetIndex, 0, draggedNeed);

  const positionMap = new Map(newNeeds.map((n, i) => [n.id, i]));
  const draggedNewIndex = positionMap.get(draggedNeed.id)!;

  for (const sourceId of getConditionSourceIds(draggedNeed)) {
    const sourceIndex = positionMap.get(sourceId);
    if (sourceIndex !== undefined && sourceIndex > draggedNewIndex) {
      const sourceName =
        needs.find((n) => n.id === sourceId)?.label || 'sa condition';
      return {
        hasConflict: true,
        reason: `Doit rester après « ${sourceName} »`,
      };
    }
  }

  for (const need of newNeeds) {
    if (getConditionSourceIds(need).includes(draggedNeed.id)) {
      const dependantIndex = positionMap.get(need.id)!;
      if (draggedNewIndex > dependantIndex) {
        return {
          hasConflict: true,
          reason: `Doit rester avant « ${need.label} »`,
        };
      }
    }
  }

  if (detectOrderConflicts(newNeeds)) {
    return { hasConflict: true, reason: 'Conflit avec une condition existante' };
  }

  return { hasConflict: false, reason: '' };
}

/**
 * Finds the first pair of fields in conflict (a conditional field placed
 * before its source). Returns the labels of both fields, or null if no
 * conflict exists. Used to generate specific toast messages on auto-sort.
 * Updated Mar 2026: checks all condition sources.
 */
export function findFirstOrderConflict(
  needs: NeedItem[]
): { conditionalLabel: string; sourceLabel: string } | null {
  const positionMap = new Map(needs.map((n, i) => [n.id, i]));
  for (const need of needs) {
    for (const sourceId of getConditionSourceIds(need)) {
      const sourceIndex = positionMap.get(sourceId);
      const ownIndex = positionMap.get(need.id)!;
      if (sourceIndex !== undefined && sourceIndex > ownIndex) {
        const sourceField = needs.find((n) => n.id === sourceId);
        return {
          conditionalLabel: need.label,
          sourceLabel: sourceField?.label || 'sa condition',
        };
      }
    }
  }
  return null;
}

/**
 * Builds a map from each source field ID to the list of IDs of fields
 * that are conditioned on it. Used for rendering dependency connectors.
 * Updated Mar 2026: supports multi-source conditions.
 */
export function getConditionDependencyMap(needs: NeedItem[]): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const need of needs) {
    for (const sourceId of getConditionSourceIds(need)) {
      const existing = map.get(sourceId) ?? [];
      map.set(sourceId, [...existing, need.id]);
    }
  }
  return map;
}
